import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { format, startOfDay } from 'date-fns';
import { Mail, Phone } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import DashboardKPITile from '@/components/DashboardKPITile';
import ConfirmationWorkModal from '@/components/confirmations/ConfirmationWorkModal';
import { depositChip, waiverChip } from '@/components/confirmations/chips';
import {
  buildConfirmationDay,
  weekDayStrip,
  type ConfirmationDay,
  type ConfirmationRow,
} from '@/domain/confirmationWorklist';
import { ensureConfirmationCall, resolveConfirmationOutcome } from '@/domain/confirmationFlow';
import { buildConfirmationCadence } from '@/utils/confirmationTemplates';
import { buildTopupTrack, sendTopupReminder, type TopupRow } from '@/domain/topupReminders';
import { aud, customerName, simTimeFull } from '@/utils/format';
import type { ConversationEntry, StudioId } from '@/domain/types';

type FilterKey = 'all' | 'calls' | 'texts' | 'late' | 'topups';

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'calls', label: 'Calls' },
  { key: 'texts', label: 'Texts' },
  { key: 'late', label: 'Late' },
  { key: 'topups', label: 'Top-ups' },
];

const SESSION_LENGTH_LABEL: Record<string, string> = {
  small: 'Small',
  'half-day': 'Half-day',
  'full-day': 'Full-day',
  'multi-session': 'Multi-session',
};

function inScopeStudio(studioId: StudioId, assignedStudios: StudioId[]): boolean {
  return assignedStudios.length === 0 || assignedStudios.includes(studioId);
}

/** The confirmation touch channel a row's lane implies, for minting on open. */
function laneKind(row: ConfirmationRow): 'sms' | 'call' {
  return row.lane === 'two-day' ? 'sms' : 'call';
}

export default function ConfirmationCalls() {
  const bookings = useBrainStore((s) => s.bookings);
  const confirmationCalls = useBrainStore((s) => s.confirmationCalls);
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const deposits = useBrainStore((s) => s.deposits);
  const waivers = useBrainStore((s) => s.waivers);
  const addConvo = useBrainStore((s) => s.addConversationEntry);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const persona = useSessionStore((s) => s.persona);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);

  const days = useMemo(() => weekDayStrip(clockIso), [clockIso]);
  const [selectedDayIso, setSelectedDayIso] = useState(() => days[0]!);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [openCallId, setOpenCallId] = useState<string | null>(null);

  // The sim clock advances (day rollover), so a selected day can fall off the
  // 7-day strip. Snap back to today whenever that happens - keeps a tab active
  // and the KPIs/queue aligned to a day that actually exists in the rail.
  useEffect(() => {
    if (!days.includes(selectedDayIso)) setSelectedDayIso(days[0]!);
  }, [days, selectedDayIso]);

  const day = useMemo(
    () =>
      buildConfirmationDay({
        nowIso: selectedDayIso,
        bookings,
        confirmationCalls,
        studioIds: assignedStudios,
      }),
    [selectedDayIso, bookings, confirmationCalls, assignedStudios],
  );

  // Today's own worklist, independent of whichever day tab is selected - the
  // "Due today" KPI always reflects today, not the tab currently being worked.
  const todayDay = useMemo(
    () =>
      buildConfirmationDay({
        nowIso: clockIso,
        bookings,
        confirmationCalls,
        studioIds: assignedStudios,
      }),
    [clockIso, bookings, confirmationCalls, assignedStudios],
  );

  // Per-day counts for the rail's badges - one buildConfirmationDay call per
  // day anchor, memoised together so re-renders don't recompute the whole week.
  const dayCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const iso of days) {
      const d: ConfirmationDay =
        iso === selectedDayIso
          ? day
          : buildConfirmationDay({ nowIso: iso, bookings, confirmationCalls, studioIds: assignedStudios });
      map.set(iso, d.counts.total);
    }
    return map;
  }, [days, day, selectedDayIso, bookings, confirmationCalls, assignedStudios]);

  const weekDueTotal = useMemo(
    () => Array.from(dayCounts.values()).reduce((sum, n) => sum + n, 0),
    [dayCounts],
  );

  // "Worked" has no stored timestamp on ConfirmationCall - it's derived here as
  // calls/texts whose touch was scheduled inside the window and already has an
  // outcome recorded (so it has dropped out of the still-open worklist above).
  const workedInRange = useMemo(() => {
    return (startMs: number, endMs: number): number =>
      Object.values(confirmationCalls).filter((c) => {
        if (c.outcome === undefined) return false;
        const b = bookings[c.bookingId];
        if (!b || !inScopeStudio(b.studioId, assignedStudios)) return false;
        const t = new Date(c.scheduledFor).getTime();
        return t >= startMs && t < endMs;
      }).length;
  }, [confirmationCalls, bookings, assignedStudios]);

  const workedToday = useMemo(() => {
    const start = startOfDay(new Date(clockIso)).getTime();
    return workedInRange(start, start + 24 * 60 * 60 * 1000);
  }, [workedInRange, clockIso]);

  const weekDone = useMemo(() => {
    const start = startOfDay(new Date(days[0]!)).getTime();
    const end = startOfDay(new Date(days[days.length - 1]!)).getTime() + 24 * 60 * 60 * 1000;
    return workedInRange(start, end);
  }, [workedInRange, days]);

  const rowKey = (row: ConfirmationRow): string => row.call?.id ?? row.booking.id;

  // Every row must open the work modal - a "call" is just the confirmation
  // touch (phone or text) about to be made. A row with no ConfirmationCall yet
  // (e.g. an unconfirmed booking that never had a touch scheduled) mints one
  // on open via ensureConfirmationCall; that record IS the touch being made.
  const openRow = (row: ConfirmationRow) => {
    const id = row.call?.id ?? ensureConfirmationCall(row.booking.id, { at: clockIso, kind: laneKind(row) });
    if (id) setOpenCallId(id);
  };

  // ASSUMPTION: placeholder copy until Georgina's template
  const onApproveAllTexts = () => {
    for (const row of day.texts) {
      const call = row.call;
      if (!call) continue;
      const booking = row.booking;
      const customer = customers[booking.customerId];
      const studio = studios[booking.studioId];
      if (!customer || !studio) continue;
      const waiver = Object.values(waivers)
        .filter((w) => w.bookingId === booking.id)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      const cadence = buildConfirmationCadence({
        booking,
        customer,
        artist: artists[booking.artistId],
        studio,
        waiverStatus: waiver?.status,
      });
      const twoDayStep = cadence.find((step) => step.stage === 'two-day');
      if (!twoDayStep) continue;

      // Mirror CustomerComposer's send path: a conversation entry + an
      // activity-feed entry via the same store actions, then resolve the
      // confirmation outcome.
      const entry: ConversationEntry = {
        id: `msg-conf-${call.id}`,
        customerId: customer.id,
        channel: 'sms',
        direction: 'out',
        body: twoDayStep.body,
        timestamp: clockIso,
        authorRole: 'agent',
        authorName: 'You',
      };
      addConvo(customer.id, entry);
      appendActivity({
        id: `act-conf-${call.id}`,
        type: 'sms-sent',
        customerId: customer.id,
        body: `2-day confirmation text sent to ${customerName(customer)}.`,
        timestamp: clockIso,
      });
      resolveConfirmationOutcome(call.id, 'confirmed', { by: persona ?? 'system', at: clockIso });
    }
  };

  // K2: the top-up reminder track. Not day-scoped (it is balance-driven), so it
  // lives on Today. `dueTopups` are the ones the cadence says to send now; sending
  // stamps the deposit + appends the SMS, so the row leaves the lane next render.
  const topupTrack = useMemo(
    () => buildTopupTrack({ nowIso: clockIso, deposits, bookings, customers, studioIds: assignedStudios }),
    [clockIso, deposits, bookings, customers, assignedStudios],
  );
  const dueTopups = useMemo(() => topupTrack.filter((r) => r.dueForReminder), [topupTrack]);
  const isToday = selectedDayIso === days[0];

  const onSendAllTopups = () => {
    for (const r of dueTopups) sendTopupReminder(r.depositId);
  };

  const showChase = filter === 'all' || filter === 'late';
  const showCalls = filter === 'all' || filter === 'calls';
  const showTexts = filter === 'all' || filter === 'texts';
  const showTopups = (filter === 'all' || filter === 'topups') && isToday;

  return (
    <section className="px-8 py-8" data-testid="confirmation-calls">
      <header className="flex items-end justify-between max-w-6xl flex-wrap gap-6">
        <div>
          <p className="label-small-caps text-gold-bright">Confirmations</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Confirm the week ahead</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-[52ch]">
            Work today&apos;s queue to zero: call, text, chase. Every item resolves to one
            outcome.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <DashboardKPITile
            label="Due today"
            value={todayDay.counts.total.toString()}
            accent
            caption="calls + texts + chases"
          />
          <DashboardKPITile
            label="Late / unconfirmed"
            value={day.chase.length.toString()}
            caption="at risk, across the week"
          />
          <DashboardKPITile
            label="Worked today"
            value={workedToday.toString()}
            caption={`of ${todayDay.counts.total + workedToday} · to zero`}
          />
        </div>
      </header>

      <div className="mt-6 max-w-6xl grid grid-cols-[172px_1fr] gap-5 items-start">
        {/* Day rail */}
        <nav className="flex flex-col gap-1" data-testid="conf-day-rail">
          <span className="label-small-caps px-2.5 pb-2">This week</span>
          {days.map((iso, idx) => {
            const active = iso === selectedDayIso;
            const label = idx === 0 ? `Today · ${format(new Date(iso), 'EEE d')}` : format(new Date(iso), 'EEE d');
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelectedDayIso(iso)}
                data-testid={`conf-day-tab-${iso}`}
                className={clsx(
                  'flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-inter text-left transition',
                  active
                    ? 'bg-ink-deep border border-gold-bright/60 text-cream'
                    : 'text-cream-soft hover:bg-ink-soft/40',
                )}
              >
                <span>{label}</span>
                <span className="font-mono text-[11px] text-cream-faint bg-ink-black/40 rounded-full px-2 py-0.5">
                  {dayCounts.get(iso) ?? 0}
                </span>
              </button>
            );
          })}
          <div className="mt-2 pt-2 border-t border-dashed border-gold-dim/40 px-2.5 text-[11px] font-inter text-cream-faint">
            Week to zero · <strong className="text-cream-soft">{weekDueTotal}</strong> due,{' '}
            <strong className="text-cream-soft">{weekDone}</strong> done
          </div>
        </nav>

        {/* Filters + queue */}
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="label-small-caps mr-0.5">Filter</span>
            {FILTERS.map((f) => {
              const count =
                f.key === 'all'
                  ? day.counts.total
                  : f.key === 'calls'
                    ? day.counts.calls
                    : f.key === 'texts'
                      ? day.counts.texts
                      : f.key === 'topups'
                        ? dueTopups.length
                        : day.counts.chase;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  data-testid={`conf-filter-${f.key}`}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-inter font-medium transition',
                    active
                      ? f.key === 'late'
                        ? 'border-blood/60 text-blood bg-blood/10'
                        : 'border-gold-bright/60 text-gold-bright bg-gold-bright/10'
                      : 'border-gold-dim/40 text-cream-soft hover:border-gold-bright/50',
                  )}
                >
                  {f.label} <span className="font-mono text-[10px] opacity-75">{count}</span>
                </button>
              );
            })}
          </div>

          {showChase ? (
            <ConfirmationGroup
              testId="conf-group-chase"
              tone="late"
              title="Needs chasing · unconfirmed & overdue"
              note="Still-live bookings (held, pending deposit, or confirmed) that are overdue: either a scheduled call or text was missed, or the session is today or already past and the client has not confirmed."
              rows={day.chase}
              rowKey={rowKey}
              onRowClick={openRow}
              customers={customers}
              artists={artists}
              studios={studios}
              deposits={deposits}
              waivers={waivers}
            />
          ) : null}

          {showCalls ? (
            <ConfirmationGroup
              testId="conf-group-calls"
              tone="default"
              title="Calls to make · sequence calls"
              rows={day.calls}
              rowKey={rowKey}
              onRowClick={openRow}
              customers={customers}
              artists={artists}
              studios={studios}
              deposits={deposits}
              waivers={waivers}
            />
          ) : null}

          {showTexts ? (
            <ConfirmationGroup
              testId="conf-group-texts"
              tone="default"
              title="Texts to approve · 2-day reminder"
              rows={day.texts}
              rowKey={rowKey}
              onRowClick={openRow}
              customers={customers}
              artists={artists}
              studios={studios}
              deposits={deposits}
              waivers={waivers}
              batchAction={
                day.texts.length > 0 ? (
                  <button
                    type="button"
                    onClick={onApproveAllTexts}
                    className="btn-ghost text-[10px] px-3 py-1"
                    data-testid="conf-approve-all-texts"
                  >
                    Approve all
                  </button>
                ) : undefined
              }
            />
          ) : null}

          {showTopups ? (
            <TopupLane
              rows={dueTopups}
              studios={studios}
              onSend={(depositId) => sendTopupReminder(depositId)}
              onSendAll={onSendAllTopups}
            />
          ) : null}
        </div>
      </div>

      {openCallId ? <ConfirmationWorkModal callId={openCallId} onClose={() => setOpenCallId(null)} /> : null}
    </section>
  );
}

interface ConfirmationGroupProps {
  testId: string;
  tone: 'late' | 'default';
  title: string;
  note?: string;
  rows: ConfirmationRow[];
  rowKey: (row: ConfirmationRow) => string;
  onRowClick: (row: ConfirmationRow) => void;
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  artists: ReturnType<typeof useBrainStore.getState>['artists'];
  studios: ReturnType<typeof useBrainStore.getState>['studios'];
  deposits: ReturnType<typeof useBrainStore.getState>['deposits'];
  waivers: ReturnType<typeof useBrainStore.getState>['waivers'];
  batchAction?: React.ReactNode;
}

function ConfirmationGroup({
  testId,
  tone,
  title,
  note,
  rows,
  rowKey,
  onRowClick,
  customers,
  artists,
  studios,
  deposits,
  waivers,
  batchAction,
}: ConfirmationGroupProps) {
  if (rows.length === 0) return null;
  return (
    <div className="mb-5" data-testid={testId}>
      <div className="flex items-baseline gap-2 mb-1">
        <h3
          className={clsx(
            'label-small-caps',
            tone === 'late' ? 'text-blood' : 'text-gold-bright',
          )}
        >
          {title}
        </h3>
        <span className="font-mono text-[11px] text-cream-faint">{rows.length}</span>
        <span className="flex-1 h-px bg-gold-dim/30" />
        {batchAction}
      </div>
      {note ? <p className="font-serif text-xs text-cream-soft mb-2">{note}</p> : null}
      <ol className="space-y-1.5">
        {rows.map((row) => (
          <ConfirmationRowItem
            key={rowKey(row)}
            row={row}
            testId={`conf-row-${rowKey(row)}`}
            onClick={() => onRowClick(row)}
            customer={customers[row.booking.customerId]}
            artist={artists[row.booking.artistId]}
            studio={studios[row.booking.studioId]}
            deposit={row.booking.depositId ? deposits[row.booking.depositId] : undefined}
            waiver={Object.values(waivers)
              .filter((w) => w.bookingId === row.booking.id)
              .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]}
          />
        ))}
      </ol>
    </div>
  );
}

interface ConfirmationRowItemProps {
  row: ConfirmationRow;
  testId: string;
  onClick: () => void;
  customer: ReturnType<typeof useBrainStore.getState>['customers'][string] | undefined;
  artist: ReturnType<typeof useBrainStore.getState>['artists'][string] | undefined;
  studio: ReturnType<typeof useBrainStore.getState>['studios'][string] | undefined;
  deposit: ReturnType<typeof useBrainStore.getState>['deposits'][string] | undefined;
  waiver: ReturnType<typeof useBrainStore.getState>['waivers'][string] | undefined;
}

function ConfirmationRowItem({
  row,
  testId,
  onClick,
  customer,
  artist,
  studio,
  deposit,
  waiver,
}: ConfirmationRowItemProps) {
  const { booking, call } = row;
  const name = customer ? customerName(customer) : booking.customerId.slice(-6);
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isCall = call?.kind === 'call' || (!call && laneKind(row) === 'call');
  const sessionLabel = SESSION_LENGTH_LABEL[booking.sessionLength] ?? booking.sessionLength;

  const depositInfo = depositChip(booking, deposit);
  const waiverInfo = waiverChip(waiver);

  const statePill = (() => {
    if (row.overdue) return { label: 'Overdue', cls: 'pill-blood' };
    if (row.atRisk) return { label: 'Refillable', cls: 'pill-blood' };
    if (row.lane === 'day-of' && booking.confirmedAt) {
      return { label: 'Monitor', cls: 'pill-mint' };
    }
    return null;
  })();

  return (
    <li
      data-testid={testId}
      onClick={onClick}
      className={clsx(
        'panel p-2.5 flex items-center gap-3 flex-wrap cursor-pointer transition hover:border-gold-bright/50',
        row.overdue && 'border-blood/50',
      )}
    >
      <span className="shrink-0 w-8 h-8 rounded-full bg-ink-black border border-gold-dim/40 flex items-center justify-center font-serif text-[11px] text-gold-bright">
        {initials || '?'}
      </span>
      <div className="flex-1 min-w-[180px]">
        <p className="font-inter text-sm text-cream flex items-center gap-2 flex-wrap">
          {name}
          <span
            className={clsx(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-widest',
              isCall ? 'bg-azure/12 text-azure' : 'bg-cream-soft/15 text-cream-soft',
            )}
          >
            {isCall ? <Phone size={9} /> : <Mail size={9} />}
            {isCall ? 'Call' : 'SMS'}
          </span>
          {studio ? (
            <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
              {studio.name}
            </span>
          ) : null}
        </p>
        <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1 flex items-center gap-1.5 flex-wrap">
          <span>
            {simTimeFull(booking.start)} · {sessionLabel}
          </span>
          <span className={clsx(depositInfo.cls, '!text-[9px] !px-1.5 !py-0')}>{depositInfo.label}</span>
          <span className={clsx(waiverInfo.cls, '!text-[9px] !px-1.5 !py-0')}>{waiverInfo.label}</span>
        </p>
      </div>
      {statePill ? (
        <span className={clsx(statePill.cls, 'text-[10px]')} data-testid={`${testId}-state`}>
          {statePill.label}
        </span>
      ) : null}
      {artist ? (
        <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest hidden lg:inline">
          {artist.displayName}
        </span>
      ) : null}
    </li>
  );
}

/**
 * K2 top-up reminder lane. A deposit-driven track (not day-scoped): the partial
 * deposits whose cadence says a reminder is due now. One tap sends and stamps the
 * deposit, so the row drops out next render. Styled to match ConfirmationGroup.
 */
function TopupLane({
  rows,
  studios,
  onSend,
  onSendAll,
}: {
  rows: TopupRow[];
  studios: Record<string, { name: string }>;
  onSend: (depositId: string) => void;
  onSendAll: () => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div
      data-testid="conf-group-topups"
      className="mb-5 rounded-xl border border-gold-bright/40 bg-gold-bright/5 p-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <h3 className="label-small-caps text-gold-bright">Top-ups · outstanding deposits</h3>
        <span className="font-mono text-[11px] text-cream-faint">{rows.length}</span>
        <button
          type="button"
          onClick={onSendAll}
          className="btn-ghost text-[10px] px-3 py-1 ml-auto"
          data-testid="conf-send-all-topups"
        >
          Send all reminders
        </button>
      </div>
      <p className="font-serif text-[12px] text-cream-soft mb-2">
        One tap texts the outstanding balance + session date, and logs it. A reply or full payment stops the track.
      </p>
      <div className="divider-gold mb-2" />
      <ol className="flex flex-col gap-2">
        {rows.map((r) => (
          <li
            key={r.depositId}
            data-testid={`conf-topup-row-${r.depositId}`}
            className="flex items-center gap-3 rounded-lg border border-gold-dim/25 bg-ink-black/30 px-3 py-2"
          >
            <span className="min-w-0">
              <span className="font-inter text-sm text-cream">{r.customerName}</span>
              <span className="flex items-center gap-1.5 text-[11px] text-cream-faint mt-0.5 flex-wrap">
                <span className="rounded bg-azure/15 text-azure px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
                  SMS
                </span>
                {r.state === 'final-due' ? (
                  <span className="rounded bg-blood/10 text-blood border border-blood/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
                    Final · T-7
                  </span>
                ) : null}
                <span>{studios[r.studioId]?.name ?? r.studioId}</span>
                {r.sessionIso ? <span>· {format(new Date(r.sessionIso), 'EEE d MMM')}</span> : null}
                {r.daysUntilSession != null ? <span>· {r.daysUntilSession}d</span> : null}
                <span>
                  · last reminder {r.lastReminderAt ? format(new Date(r.lastReminderAt), 'd MMM') : 'none yet'}
                </span>
              </span>
            </span>
            <span className="ml-auto text-right whitespace-nowrap">
              <span className="block font-mono tabular-nums text-sm font-semibold text-gold-bright">
                {aud(r.outstandingAud)}
              </span>
              <span className="block text-[10px] text-cream-faint">
                of {aud(r.sentAud)} · paid {aud(r.paidAud)}
              </span>
            </span>
            <button
              type="button"
              onClick={() => onSend(r.depositId)}
              data-testid={`conf-topup-send-${r.depositId}`}
              className="rounded-md border border-gold-bright/50 bg-gold-bright/10 px-3 py-1.5 text-[12px] font-semibold text-gold-bright hover:bg-gold-bright/20 whitespace-nowrap"
            >
              Send reminder
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
